import { Vector3, Camera, Group } from 'three';
import type { GameState } from '../core/GameState';
import type { AgentState, TaskState } from '../types';

const TASK_TYPE_NAMES: Record<string, string> = {
  'color-sequence': 'COLOR',
  'shape-match': 'SHAPES',
  'pattern-grid': 'PATTERN',
  'signal-flow': 'SIGNAL',
  'symmetry': 'MIRROR',
};

export class WorldLabels {
  private container: HTMLElement;
  private labelsContainer: HTMLElement;
  private camera: Camera;
  private canvas: HTMLCanvasElement;

  // Cursor tooltip
  private tooltip: HTMLElement;

  // Connection lines (SVG)
  private svgOverlay: SVGSVGElement;

  // Cached label elements per entity
  private agentLabels = new Map<string, HTMLElement>();
  private taskLabels = new Map<string, HTMLElement>();

  // Interaction state
  private selectedTaskId: string | null = null;
  private hoveredType: 'agent' | 'task' | null = null;
  private hoveredId: string | null = null;

  constructor(container: HTMLElement, camera: Camera, canvas: HTMLCanvasElement) {
    this.container = container;
    this.camera = camera;
    this.canvas = canvas;

    // Labels container
    this.labelsContainer = document.createElement('div');
    this.labelsContainer.className = 'world-labels';
    container.appendChild(this.labelsContainer);

    // SVG overlay for connection lines
    this.svgOverlay = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svgOverlay.setAttribute('class', 'connection-lines');
    container.appendChild(this.svgOverlay);

    // Tooltip
    this.tooltip = document.createElement('div');
    this.tooltip.className = 'cursor-tooltip';
    container.appendChild(this.tooltip);

    // Track mouse for tooltip
    canvas.addEventListener('pointermove', (e) => {
      const rect = canvas.getBoundingClientRect();
      this.tooltip.style.left = `${e.clientX - rect.left + 16}px`;
      this.tooltip.style.top = `${e.clientY - rect.top - 8}px`;
    });
  }

  setHover(type: 'agent' | 'task' | null, id: string | null): void {
    this.hoveredType = type;
    this.hoveredId = id;
  }

  setSelectedTask(taskId: string | null): void {
    this.selectedTaskId = taskId;
  }

  update(state: GameState): void {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;

    // Update agent labels
    const activeAgentIds = new Set<string>();
    for (const agent of state.agents) {
      activeAgentIds.add(agent.id);
      let label = this.agentLabels.get(agent.id);
      if (!label) {
        label = this.createAgentLabel();
        this.labelsContainer.appendChild(label);
        this.agentLabels.set(agent.id, label);
      }
      this.updateAgentLabel(label, agent, w, h);
    }

    // Remove stale agent labels
    for (const [id, label] of this.agentLabels) {
      if (!activeAgentIds.has(id)) {
        label.remove();
        this.agentLabels.delete(id);
      }
    }

    // Update task labels
    const activeTaskIds = new Set<string>();
    for (const task of state.taskQueue) {
      activeTaskIds.add(task.id);
      let label = this.taskLabels.get(task.id);
      if (!label) {
        label = this.createTaskLabel();
        this.labelsContainer.appendChild(label);
        this.taskLabels.set(task.id, label);
      }
      this.updateTaskLabel(label, task, w, h);
    }

    // Remove stale task labels
    for (const [id, label] of this.taskLabels) {
      if (!activeTaskIds.has(id)) {
        label.remove();
        this.taskLabels.delete(id);
      }
    }

    // Update tooltip
    this.updateTooltip(state);

    // Update connection lines
    this.updateConnections(state, w, h);
  }

  private projectToScreen(pos: Vector3, w: number, h: number): { x: number; y: number; visible: boolean } {
    const projected = pos.clone().project(this.camera);
    const x = (projected.x * 0.5 + 0.5) * w;
    const y = (-projected.y * 0.5 + 0.5) * h;
    const visible = projected.z > 0 && projected.z < 1;
    return { x, y, visible };
  }

  private createAgentLabel(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'world-label agent-label';
    el.innerHTML = `
      <div class="wl-title"></div>
      <div class="wl-status"></div>
      <div class="wl-drift-bar"><div class="wl-drift-fill"></div></div>
    `;
    return el;
  }

  private updateAgentLabel(el: HTMLElement, agent: AgentState, w: number, h: number): void {
    if (!agent.mesh) { el.style.display = 'none'; return; }

    const worldPos = new Vector3();
    agent.mesh.getWorldPosition(worldPos);
    worldPos.y += 2.2; // above the agent

    const screen = this.projectToScreen(worldPos, w, h);
    if (!screen.visible) { el.style.display = 'none'; return; }

    el.style.display = '';
    el.style.left = `${screen.x}px`;
    el.style.top = `${screen.y}px`;

    const title = el.querySelector('.wl-title') as HTMLElement;
    const status = el.querySelector('.wl-status') as HTMLElement;
    const driftFill = el.querySelector('.wl-drift-fill') as HTMLElement;

    const agentNum = agent.slotIndex + 1;
    title.textContent = `AGENT #${agentNum}`;

    // Status text — make it very clear what the agent is doing
    let statusText = '';
    let statusClass = '';

    if (this.selectedTaskId && agent.status === 'idle') {
      // Task is selected and this agent is idle — show assign prompt
      statusText = '▶ CLICK TO ASSIGN TASK';
      statusClass = 'status-assignable';
    } else {
      switch (agent.status) {
        case 'idle':
          statusText = 'IDLE — WAITING';
          statusClass = 'status-idle';
          break;
        case 'working': {
          const pct = Math.round(agent.taskProgress * 100);
          const taskName = agent.currentTask
            ? (TASK_TYPE_NAMES[agent.currentTask.type] || agent.currentTask.type)
            : '';
          const reward = agent.currentTask
            ? (agent.currentTask.priority ? '75' : '25')
            : '?';
          statusText = `${taskName} ${pct}% → +${reward} compute`;
          statusClass = agent.drift > 0.5 ? 'status-danger' : 'status-working';
          break;
        }
        case 'presenting':
          statusText = '✅ DONE — CLICK TO VALIDATE';
          statusClass = 'status-presenting';
          break;
        case 'crashed':
          statusText = 'CRASHED';
          statusClass = 'status-crashed';
          break;
      }

      if (agent.drift > 0.5 && agent.status === 'working') {
        statusText = `⚠ DRIFT ${Math.round(agent.drift * 100)}% — CLICK TO CORRECT`;
        statusClass = 'status-danger';
      }

      if (agent.correctionCooldown > 0 && agent.drift > 0.1) {
        statusText += ` (cd ${Math.ceil(agent.correctionCooldown)}s)`;
      }
    }

    status.textContent = statusText;
    status.className = `wl-status ${statusClass}`;

    // Drift bar
    const driftPct = Math.round(agent.drift * 100);
    driftFill.style.width = `${driftPct}%`;
    if (agent.drift < 0.3) driftFill.style.background = '#4ecdc4';
    else if (agent.drift < 0.6) driftFill.style.background = '#fb923c';
    else driftFill.style.background = '#ff6b6b';

    // Highlight if hovered
    if (this.hoveredType === 'agent' && this.hoveredId === agent.id) {
      el.classList.add('hovered');
    } else {
      el.classList.remove('hovered');
    }

    // Highlight idle agents when a task is selected
    if (this.selectedTaskId && agent.status === 'idle') {
      el.classList.add('assignable');
    } else {
      el.classList.remove('assignable');
    }
  }

  private createTaskLabel(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'world-label task-label';
    el.innerHTML = `
      <div class="wl-title"></div>
      <div class="wl-timer"></div>
    `;
    return el;
  }

  private updateTaskLabel(el: HTMLElement, task: TaskState, w: number, h: number): void {
    if (!task.mesh) { el.style.display = 'none'; return; }

    const group = task.mesh.parent;
    if (!group) { el.style.display = 'none'; return; }

    const worldPos = new Vector3();
    group.getWorldPosition(worldPos);
    worldPos.y += 1.2;

    const screen = this.projectToScreen(worldPos, w, h);
    if (!screen.visible) { el.style.display = 'none'; return; }

    el.style.display = '';
    el.style.left = `${screen.x}px`;
    el.style.top = `${screen.y}px`;

    const title = el.querySelector('.wl-title') as HTMLElement;
    const timer = el.querySelector('.wl-timer') as HTMLElement;

    const typeName = TASK_TYPE_NAMES[task.type] || task.type.toUpperCase();
    title.textContent = `📋 ${typeName}`;
    if (task.priority) title.textContent = `⭐ ${typeName}`;

    timer.textContent = `${Math.ceil(task.urgency)}s`;
    if (task.urgency < 5) timer.classList.add('urgent');
    else timer.classList.remove('urgent');

    // Selected state
    if (this.selectedTaskId === task.id) {
      el.classList.add('selected');
    } else {
      el.classList.remove('selected');
    }

    if (this.hoveredType === 'task' && this.hoveredId === task.id) {
      el.classList.add('hovered');
    } else {
      el.classList.remove('hovered');
    }
  }

  private updateTooltip(state: GameState): void {
    if (state.phase !== 'playing') {
      this.tooltip.style.display = 'none';
      return;
    }

    let text = '';

    if (this.hoveredType === 'task' && !this.selectedTaskId) {
      text = '① CLICK TO SELECT TASK';
    } else if (this.hoveredType === 'task' && this.selectedTaskId) {
      text = 'TASK ALREADY SELECTED';
    } else if (this.hoveredType === 'agent' && this.selectedTaskId) {
      const agent = state.agents.find(a => a.id === this.hoveredId);
      if (agent?.status === 'idle') {
        text = '② CLICK TO ASSIGN TASK HERE';
      } else {
        text = 'AGENT BUSY';
      }
    } else if (this.hoveredType === 'agent') {
      const agent = state.agents.find(a => a.id === this.hoveredId);
      if (agent?.status === 'presenting') {
        text = 'CLICK TO VALIDATE & COLLECT';
      } else if (agent && agent.drift > 0.1 && agent.correctionCooldown <= 0) {
        text = 'CLICK TO CORRECT DRIFT';
      } else if (agent && agent.correctionCooldown > 0) {
        text = 'COOLDOWN...';
      }
    }

    if (text) {
      this.tooltip.textContent = text;
      this.tooltip.style.display = 'block';
    } else {
      this.tooltip.style.display = 'none';
    }
  }

  private updateConnections(state: GameState, w: number, h: number): void {
    // Clear existing lines
    while (this.svgOverlay.firstChild) {
      this.svgOverlay.removeChild(this.svgOverlay.firstChild);
    }
    this.svgOverlay.setAttribute('width', String(w));
    this.svgOverlay.setAttribute('height', String(h));

    if (!this.selectedTaskId) return;

    // Find selected task position
    const task = state.taskQueue.find(t => t.id === this.selectedTaskId);
    if (!task?.mesh?.parent) return;

    const taskPos = new Vector3();
    task.mesh.parent.getWorldPosition(taskPos);
    taskPos.y += 1;
    const taskScreen = this.projectToScreen(taskPos, w, h);
    if (!taskScreen.visible) return;

    // Draw dashed lines to idle agents
    for (const agent of state.agents) {
      if (agent.status !== 'idle' || !agent.mesh) continue;

      const agentPos = new Vector3();
      agent.mesh.getWorldPosition(agentPos);
      agentPos.y += 1;
      const agentScreen = this.projectToScreen(agentPos, w, h);
      if (!agentScreen.visible) continue;

      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', String(taskScreen.x));
      line.setAttribute('y1', String(taskScreen.y));
      line.setAttribute('x2', String(agentScreen.x));
      line.setAttribute('y2', String(agentScreen.y));
      line.setAttribute('class', 'connection-line');
      this.svgOverlay.appendChild(line);
    }
  }

  /** Add a pulsing highlight ring at a world position (for tutorial) */
  highlightWorldPosition(pos: Vector3, label?: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'tutorial-highlight';
    if (label) {
      el.innerHTML = `<div class="th-ring"></div><div class="th-label">${label}</div>`;
    } else {
      el.innerHTML = `<div class="th-ring"></div>`;
    }
    this.labelsContainer.appendChild(el);
    return el;
  }

  clearAll(): void {
    for (const [, label] of this.agentLabels) label.remove();
    for (const [, label] of this.taskLabels) label.remove();
    this.agentLabels.clear();
    this.taskLabels.clear();
    while (this.svgOverlay.firstChild) this.svgOverlay.removeChild(this.svgOverlay.firstChild);
    this.tooltip.style.display = 'none';
    this.selectedTaskId = null;
    this.hoveredType = null;
    this.hoveredId = null;
  }

  dispose(): void {
    this.labelsContainer.remove();
    this.svgOverlay.remove();
    this.tooltip.remove();
  }
}
